import styled from 'styled-components'

export const Container = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  margin: 32px 0;
`

export const ErrorText = styled.p`
  margin: 0;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.action.danger.bg}33;
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(220, 38, 38, 0.12)'
      : 'rgba(220, 38, 38, 0.06)'};
  color: ${({ theme }) => theme.colors.action.danger.bg};
  font-size: 0.875rem;
  font-weight: 500;
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1.5rem;
  border-radius: 14px;
  border: 1px dashed ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  text-align: center;

  strong {
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  span {
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

export const FilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? `${theme.colors.action.primary.bg}55`
        : theme.colors.borderDefault};
  background: ${({ $active, theme }) => {
    if (!$active) return theme.colors.background
    return theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.16)'
      : 'rgba(62, 207, 142, 0.1)'
  }};
  color: ${({ $active, theme }) =>
    $active
      ? theme.colors.action.primary.bg
      : theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => `${theme.colors.action.primary.bg}55`};
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const FilterCount = styled.span`
  font-weight: 500;
  opacity: 0.85;
`

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

export const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const SectionHint = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`

export const UserCard = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.85rem;
  width: 100%;
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) =>
      theme.colors.borderStrong || theme.colors.borderDefault};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const CardMain = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex: 1;
  min-width: 0;
`

export const Avatar = styled.img`
  width: ${({ $size }) => ($size ? `${$size}px` : '52px')};
  height: ${({ $size }) => ($size ? `${$size}px` : '52px')};
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.borderSubtle || theme.colors.borderDefault};
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
`

export const CardHeader = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 0.75rem 1rem;
`

export const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
  flex: 1;
`

export const IdentityRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.4rem;
`

export const UserName = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;
`

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.12rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: transparent;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${({ $warn, theme }) =>
    $warn
      ? theme.colors.action.warning?.bg || '#d97706'
      : theme.colors.textMuted};

  ${({ $live, theme }) =>
    $live &&
    `
    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${theme.colors.action.primary.bg};
    }
  `}
`

export const UserEmail = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-word;
`

export const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, minmax(0, 1fr));
  gap: 0.65rem 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

export const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
`

export const MetaLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const MetaValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme, $muted }) =>
    $muted ? theme.colors.textMuted : theme.colors.textPrimary};
  word-break: break-word;
`

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: space-between;
    padding-top: 0.75rem;
    border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
  }
`

export const StatusToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  span {
    font-size: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`

export const DetailView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`

export const DetailToolbar = styled.div`
  display: flex;
  align-items: center;
`

export const BackButton = styled.button`
  margin: 0;
  padding: 0.35rem 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.action.primary.bg};
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const DetailCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  padding: 1rem 1.15rem;
  border-radius: 14px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
`

export const DetailHero = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem 1rem;
  flex-wrap: wrap;
`

export const DetailSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding-top: 0.85rem;
  border-top: 1px solid ${({ theme }) => theme.colors.borderDefault};
`

export const PensionYears = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`

export const PensionYearGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const PensionYearLabel = styled.h4`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const PensionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

export const PensionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? theme.colors.backgroundSecondary || theme.colors.background
      : theme.colors.borderSubtle || theme.colors.background};

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`

export const PensionMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`

export const PensionAmount = styled.div`
  font-size: 0.9375rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const PensionMeta = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`

export const PensionSide = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.65rem;
  flex-shrink: 0;

  @media (max-width: 640px) {
    justify-content: space-between;
  }
`

export const PaymentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid
    ${({ $paid, theme }) =>
      $paid
        ? `${theme.colors.action.primary.bg}33`
        : `${theme.colors.textMuted}44`};
  background: ${({ $paid, theme }) => {
    if ($paid) {
      return theme.name === 'dark'
        ? 'rgba(62, 207, 142, 0.16)'
        : 'rgba(62, 207, 142, 0.1)'
    }
    return theme.name === 'dark'
      ? 'rgba(148, 163, 184, 0.14)'
      : 'rgba(148, 163, 184, 0.12)'
  }};
  color: ${({ $paid, theme }) =>
    $paid ? theme.colors.action.primary.bg : theme.colors.textMuted};
`

export const WarningText = styled.p`
  margin: 0;
  padding: 0.85rem 1rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) =>
    `${theme.colors.action.warning?.bg || '#d97706'}44`};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(217, 119, 6, 0.16)'
      : 'rgba(217, 119, 6, 0.08)'};
  color: ${({ theme }) => theme.colors.action.warning?.bg || '#d97706'};
  font-size: 0.8125rem;
  font-weight: 500;
`

export const RoleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

export const RoleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
`

export const RoleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
`

export const RoleName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-transform: capitalize;
`

export const RoleHint = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const MenuChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

export const MenuChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid ${({ theme }) => `${theme.colors.action.primary.bg}33`};
  background: ${({ theme }) =>
    theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.16)'
      : 'rgba(62, 207, 142, 0.1)'};
  color: ${({ theme }) => theme.colors.action.primary.bg};
`

export const TextButton = styled.button`
  margin: 0;
  padding: 0.25rem 0.55rem;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.textSecondary};
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.action.primary.bg};
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const DangerButton = styled(TextButton)`
  color: ${({ theme }) => theme.colors.action.danger.bg};
  border-color: ${({ theme }) => `${theme.colors.action.danger.bg}44`};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.action.danger.bg};
    color: ${({ theme }) => theme.colors.action.danger.bg};
  }
`

export const OpsRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem 1rem;
  padding: 0.65rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderDefault};
`

export const OpsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

export const OpsMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
  flex: 1;
`

export const OpsTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;
`

export const OpsMeta = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-word;
`

export const MonoValue = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  text-align: left;
  word-break: break-all;

  &:hover {
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }
`

export const FormulaText = styled.span`
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
`

export const HintText = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`

export const ConfigEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`

export const MethodChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

export const MethodChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid
    ${({ $active, theme }) =>
      $active
        ? `${theme.colors.action.primary.bg}55`
        : theme.colors.borderDefault};
  background: ${({ $active, theme }) => {
    if (!$active) return theme.colors.background
    return theme.name === 'dark'
      ? 'rgba(62, 207, 142, 0.16)'
      : 'rgba(62, 207, 142, 0.1)'
  }};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.action.primary.bg : theme.colors.textSecondary};
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => `${theme.colors.action.primary.bg}55`};
    color: ${({ theme }) => theme.colors.action.primary.bg};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.action.primary.bg};
    outline-offset: 2px;
  }
`

export const ConfigInputWrap = styled.div`
  width: 100%;
  max-width: 12rem;
`

export const ConfigError = styled.p`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.action.danger.bg};
`

export const FormActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

